import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { BgPlaceholder, PlaceholderType } from '~/components/ui/BgPlaceholder';

import { INotificationState, rage, useObservable } from '~/rage';
import { LayoutBuilder } from '~/rage/utils';
import {
	Layouts,
	NotificationStyle,
	NotificationType,
	ProgressIcon,
} from '~/shared';

import { playSound, Sound } from '~/assets/sounds';
import {
	CounterNotification,
	InfoNotification,
	MicroStatus,
	ProgressNotification,
} from './components';

const placeholderTypes = Object.values(PlaceholderType);

const StubControls: React.FC = () => {
	const layouts = useObservable(rage.gui.getVisibleLayouts());
	const isOnlyVisible =
		layouts.length === 1 && layouts[0] === Layouts.Notification;
	const isStub = rage.config.isStub();
	const [backgroundIndex, setBackgroundIndex] = useState(0);

	const handleShowSuccessNotification = useCallback(() => {
		rage.notifications.showSuccess('Уведомление об успехе');
	}, []);

	const handleShowErrorNotification = useCallback(() => {
		rage.notifications.showError('Уведомление об ошибке');
	}, []);

	const handleShowInfoNotification = useCallback(() => {
		rage.notifications.showInfo('Информационное уведомление');
	}, []);

	const handleShowProgressNotification = useCallback(() => {
		rage.notifications.showProgress(
			'Уведомление о прогрессе',
			3000,
			ProgressIcon.Job,
		);
	}, []);

	const handleHideProgressNotification = useCallback(() => {
		rage.notifications.hideProgress();
	}, []);

	const handleCycleBackground = useCallback(() => {
		setBackgroundIndex((prev) => (prev + 1) % placeholderTypes.length);
	}, []);

	if (!isStub || !isOnlyVisible) return null;

	return (
		<>
			<div style={{ position: 'fixed', inset: 0, zIndex: -2 }}>
				<BgPlaceholder $type={placeholderTypes[backgroundIndex]} />
			</div>
			<div
				style={{
					position: 'fixed',
					top: '50%',
					left: '50%',
					transform: 'translate(-50%, -50%)',
					display: 'flex',
					flexDirection: 'column',
					gap: 8,
					padding: 8,
					background: '#111314',
					border: '1px solid rgba(255, 255, 255, 0.1)',
					borderRadius: 10,
					boxShadow: '0 0 30px rgba(0, 0, 0, 0.5)',
					zIndex: -1,
					fontFamily: "'GT Walsheim Pro', sans-serif",
				}}
			>
				<Button
					color={ButtonColors.Select}
					height={ButtonHeight.Height30}
					onClick={handleShowSuccessNotification}
				>
					Показать успех
				</Button>
				<Button
					color={ButtonColors.Cancel}
					height={ButtonHeight.Height30}
					onClick={handleShowErrorNotification}
				>
					Показать ошибку
				</Button>
				<Button
					color={ButtonColors.Help}
					height={ButtonHeight.Height30}
					onClick={handleShowInfoNotification}
				>
					Показать информацию
				</Button>
				<Button
					color={ButtonColors.Buy}
					height={ButtonHeight.Height30}
					onClick={handleShowProgressNotification}
				>
					Показать прогресс
				</Button>
				<Button
					color={ButtonColors.Default}
					height={ButtonHeight.Height30}
					onClick={handleHideProgressNotification}
				>
					Скрыть прогресс
				</Button>
				<Button
					color={ButtonColors.Select}
					height={ButtonHeight.Height30}
					onClick={handleCycleBackground}
				>
					Фон {backgroundIndex + 1}/{placeholderTypes.length}
				</Button>
			</div>
		</>
	);
};

const LayoutComponent = () => {
	const latestTopNotificationId = useRef<string | null>(null);
	const displayedNotification = useObservable(
		rage.notifications.getDisplayedNotification(),
	);
	const displayedNotifications = useObservable(
		rage.notifications.getDisplayedNotifications(),
	);

	const getNotificationKey = (notification: INotificationState): string => {
		if (notification.id !== undefined) {
			return String(notification.id);
		}

		return `${notification.type ?? NotificationType.Default}:${
			notification.text
		}:${notification.count ?? ''}:${notification.image ?? ''}`;
	};

	const progressNotification =
		displayedNotification?.duration !== undefined
			? displayedNotification
			: null;
	const stackNotifications = progressNotification ? [] : displayedNotifications;

	useEffect(() => {
		const topNotification = stackNotifications[0];
		if (!topNotification) {
			latestTopNotificationId.current = null;
			return;
		}

		const currentId = getNotificationKey(topNotification);
		if (latestTopNotificationId.current === currentId) {
			return;
		}

		latestTopNotificationId.current = currentId;
		if (topNotification.style === NotificationStyle.Error) {
			playSound(Sound.Error3);
		} else {
			playSound(Sound.Notification);
		}
	}, [stackNotifications]);

	const renderNotification = (
		notification: INotificationState,
		layerIndex: number,
	): JSX.Element => {
		if (notification.type === NotificationType.Counter) {
			return (
				<CounterNotification
					$layerIndex={layerIndex}
					text={notification.text}
					count={notification.count ?? 1}
					image={notification.image}
				/>
			);
		}

		return (
			<InfoNotification
				$layerIndex={layerIndex}
				notificationStyle={notification.style ?? NotificationStyle.Info}
				text={notification.text}
			/>
		);
	};

	const progressText = progressNotification?.text ?? '';
	const progressDuration = progressNotification?.duration ?? 0;
	const progressIcon = progressNotification?.icon;

	const stackNodes = stackNotifications.map((notification, index) => (
		<CSSTransition
			key={getNotificationKey(notification)}
			classNames="fade"
			timeout={300}
		>
			{renderNotification(notification, index)}
		</CSSTransition>
	));

	const progressNode = (
		<ProgressNotification
			$layerIndex={0}
			duration={progressDuration}
			text={progressText}
			icon={progressIcon}
		/>
	);

	const isProgressVisible = progressNotification !== null;

	return (
		<Fragment>
			<CSSTransition
				in={isProgressVisible}
				classNames="fade"
				timeout={750}
				unmountOnExit
			>
				{progressNode}
			</CSSTransition>
			<TransitionGroup component={null}>{stackNodes}</TransitionGroup>
			<MicroStatus />
			{/* <JobNotification /> @TODO implement */}
			<StubControls />
		</Fragment>
	);
};

const Layout = new LayoutBuilder(LayoutComponent)
	.setDisplayName(Layouts.Notification)
	.setHudVisible(true)
	.build();

export default Layout;
